import { ISelectOption, Tooltip } from '@aon/aon-tws-common-ui';
import { ICulture, SourceType as CommonSourceType } from '@aon/tws-common-ui-questions/build/lib/models';
import { createSelector } from '@reduxjs/toolkit';
import * as React from 'react';
import _ from 'lodash';
import { Reducer } from 'redux';
import { AppDispatch, RootState } from '../appStore';
import { DisplayTextWithDetails } from '../components/common';
import { IFollowUpQuestionOptionMax, IFollowUpQuestionOptions } from '../components/pages/logic-and-layout/table/FollowUpQuestionRow';
import Constants from '../constants';
import ContentConstants from '../contentConstants';
import {
	AudienceSourceType,
	EditState,
	ElementType,
	ExternalApplicationType,
	IAddedQuestion,
	IAudienceSourceVm,
	IExternalApplicationType,
	IExternalApplicationTypeVm,
	IPageFlowElementModel,
	ISurveyEditModel,
	ISurveyElement,
	ISurveyQuestionEditModel,
	ITranslationEditItem,
	IValidationModel,
	ProgressMeterType,
	QuestionType,
	SourceType,
	SurveyQuestionEditModel,
	SurveyStep,
	SurveyType,
	UserType,
} from '../models';
import { SurveyTermItemEditFieldType } from '../models/ISurveyTermItemEditModel';
import { PublishDestination } from '../models/PublishDestination';
import * as ClientAPI from '../services/ClientAPI';
import * as SurveyAPI from '../services/SurveyAPI';
import * as SurveyDefinitionsAPI from '../services/SurveyDefinitionsAPI';
import { defaultCulture, ExternalApplicationTypeHelper, QuestionEqualityHelper, SurveyDesignHelper, TranslationHelper } from '../utilities';
import { IApplicationState } from './';
import * as Actions from './Actions';
import { ActionTypes } from './ActionTypes';
import { loadSurveyDimensions } from './SurveyDimensionsSlice';
import * as TextToSpeechCultureStore from './TextToSpeechCultureStore';

export interface ISurveyState {
	surveyEditModel: ISurveyEditModel;
	lastSavedSurveyEditModel: ISurveyEditModel;
	showQuestionsModal: boolean;
	isLoading: boolean;
	applicationTypes: ISelectOption[];
	surveyTypes: ISelectOption[];
}
export const InitDefaultTranslationEditItem = (cultureCode?: string) => {
	let culture: ICulture = defaultCulture;
	if (cultureCode) {
		// Only CultureCode is being used for default translation
		culture = { ...culture, cultureCode, cultureId: 0, name: cultureCode };
	}
	return TranslationHelper.initializeEditItemForCultureCode(culture);
};
export const defaultEditItem: ITranslationEditItem = InitDefaultTranslationEditItem();

const defaultProgressMeterType = ProgressMeterType.PageNumber;
export const defaultSurveyEditModel: ISurveyEditModel = {
	currentStep: SurveyStep.Basics,
	defaultCultureCode: defaultCulture.cultureCode,
	survey: {
		surveyId: 0,
		clientId: 0,
		userId: 0,
		name: '',
		surveyIntro: false,
		audienceSourceType: undefined,
		isTextToSpeechEnabled: false,
		progressMeterType: defaultProgressMeterType,
		shouldShowQuestionNumbering: true,
		surveyTypes: SurveyType.Engagement,
		showKincentricLogo: false,
		contentItemsOrder: Constants.SURVEY_CONTENT_ITEM_SORT_DEFAULT,
		publishDestination: PublishDestination.mResearch,
	},
	audienceSource: {},
	title: { ...defaultEditItem },
	intro: { ...defaultEditItem },
	instructions: { ...defaultEditItem },
	textToSpeechInstructions: { ...defaultEditItem },
	supportedCultures: [defaultCulture.cultureCode],
	benchmarks: [],
	questions: [],
	pageFlowElements: [],
	progressMeterType: defaultProgressMeterType,
	shouldRandomizeFirstPage: false,
	shouldShowQuestionNumbering: true,
	privacyPolicy: {
		isActive: false,
		title: defaultEditItem,
		content: defaultEditItem,
		checkboxText: defaultEditItem,
		useClientPrivacyPolicy: false,
	},
	surveyTerm: { pageDescription: defaultEditItem, isActive: false, isLoading: false },
	surveyTermItems: [],
};

export const defaultState: ISurveyState = {
	surveyEditModel: defaultSurveyEditModel,
	lastSavedSurveyEditModel: defaultSurveyEditModel,
	showQuestionsModal: false,
	isLoading: false,
	applicationTypes: [],
	surveyTypes: [],
};

export const ActionCreators = {
	SetDefaultState: () => (dispatch: any, getState: () => IApplicationState) => {
		dispatch({ type: ActionTypes.SURVEY_SET_DEFAULT_STATE });

		if (getState().oidc.user?.profile.userType === UserType.External) {
			dispatch({ type: ActionTypes.SURVEY_SET_EXTERNAL_APPLICATION_TYPE, payload: ExternalApplicationType.MyReports });
		}
	},
	GetSurvey: (surveyId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_GET_REQUESTED });
		return SurveyAPI.getSurvey(surveyId)
			.then((surveyEditModel: IValidationModel<ISurveyEditModel>) => {
				dispatch({ type: ActionTypes.SURVEY_GET_SUCCEEDED, payload: surveyEditModel });
			})
			.catch(() => dispatch({ type: ActionTypes.SURVEY_GET_FAILED }));
	},
	GetExternalApplicationTypesIfNeeded: () => (dispatch: any, getState: () => IApplicationState) => {
		if (getState().survey.applicationTypes.length <= 0) {
			if (getState().oidc.user?.profile.userType === UserType.Internal) {
				dispatch({ type: ActionTypes.EXTERNAL_APPLICATION_TYPES_GET_REQUESTED });
				const externalClientId = getState().oidc.user?.profile.external_clientid;
				return ClientAPI.getExternalApplicationTypes(externalClientId)
					.then((appTypeVms: IExternalApplicationType[]) => {
						const applicationTypeVms: IExternalApplicationTypeVm[] = ExternalApplicationTypeHelper.addDisplayNameAndSequence(appTypeVms);
						dispatch({ type: ActionTypes.EXTERNAL_APPLICATION_TYPES_GET_SUCCEEDED, payload: applicationTypeVms });
					})
					.catch(() => dispatch({ type: ActionTypes.EXTERNAL_APPLICATION_TYPES_GET_FAILED }));
			}
		}
	},
	HandleSurveyNameChange: (e: any) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_NAME_UPDATE, payload: e.target.value });
	},
	HandleTextToSpeechChange: (e: boolean) => (dispatch: any, getState: () => IApplicationState) => {
		dispatch({ type: ActionTypes.SURVEY_TEXTTOSPEECH_UPDATE, payload: e });
		const isTextToSpeechCulturesLoaded = getState().textToSpeechCultures.isTextToSpeechCulturesLoaded;
		if (e && !isTextToSpeechCulturesLoaded) {
			dispatch(TextToSpeechCultureStore.ActionCreators.GetTextToSpeechSupportedCulturesIfNeeded());
		}
	},
	HandleAudienceSourceType: (audienceSourceType?: AudienceSourceType) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_SET_AUDIENCE_SOURCE_TYPE, payload: audienceSourceType });
		dispatch({ type: ActionTypes.STEPPER_VALIDATION_CLEARED });
	},
	HandleExternalApplicationType: (applicationType?: ExternalApplicationType) => ({
		type: ActionTypes.SURVEY_SET_EXTERNAL_APPLICATION_TYPE,
		payload: applicationType,
	}),
	HandleSurveyTypes: (value: string[]) => (dispatch: any) => {
		const surveyType = _.sum(value.map((s) => SurveyType[s]));
		dispatch({ type: ActionTypes.SURVEY_SET_SURVEY_TYPE, payload: surveyType });
	},
	HandleExternalSourceChange:
		(audienceSourceType: AudienceSourceType, id: string) => (dispatch: any, getState: () => IApplicationState) => {
			const DeriveHierarchyId = () => (audienceSourceType === AudienceSourceType.Hierarchy ? id : '');
			const DeriveHrisId = () => {
				if (audienceSourceType === AudienceSourceType.Hris) {
					return id;
				}

				if (audienceSourceType === AudienceSourceType.Hierarchy) {
					const { hierarchyMetadatas } = getState().externalHierarchy;

					if (hierarchyMetadatas instanceof Array) {
						return hierarchyMetadatas.find((h) => h.hierarchyId === id)?.hrisId ?? '';
					}
				}

				return '';
			};

			dispatch({
				type: ActionTypes.SURVEY_SET_EXTERNAL_SOURCE,
				payload: {
					externalHierarchyId: DeriveHierarchyId(),
					externalHrisId: DeriveHrisId(),
				},
			});
		},
	HandleSurveyTitleChange: (value: string) => ({ type: ActionTypes.SURVEY_TITLE_CHANGE, payload: value }),
	HandleTranslationEditorTitleUpdate: (item: ITranslationEditItem) => ({ type: ActionTypes.SURVEY_TITLE_UPDATED, payload: item }),
	HandleSurveyIntroChange: (value: string) => ({ type: ActionTypes.SURVEY_INTRO_CHANGE, payload: value }),
	HandleSurveyContentItemSortOrderChange: (newOrder: string) => {
		const newOrderArr: string[] = newOrder.split('|');
		if (newOrderArr.length < 3) {
			const defaultOrderArr = Constants.SURVEY_CONTENT_ITEM_SORT_DEFAULT.split('|');
			const missed: string[] = defaultOrderArr.filter((item: string) => newOrderArr.findIndex((x: string) => x === item) === -1);
			const fullNewOrderStr = newOrderArr.concat(missed).join('|');
			return { type: ActionTypes.SURVEY_CONTENT_ITEM_SORT, payload: fullNewOrderStr };
		} else {
			return { type: ActionTypes.SURVEY_CONTENT_ITEM_SORT, payload: newOrder };
		}
	},
	HandleSurveyInstructionsChange: (value: string) => ({ type: ActionTypes.SURVEY_INSTRUCTIONS_CHANGE, payload: value }),
	HandleTogglePrivacyPolicySwitch: (value: boolean) => ({ type: ActionTypes.SURVEY_PRIVACYPOLICY_SWITCH_UPDATE, payload: value }),
	InitializeSurveyDefinitions: (surveyId: number) => async (dispatch: any, getState: () => IApplicationState) => {
		dispatch({
			type: ActionTypes.SURVEY_DEFINITIONS_INITIALIZE_REQUESTED,
		});

		const defaultCultureCode = getState().survey.surveyEditModel.defaultCultureCode;
		try {
			await Promise.all([
				SurveyDefinitionsAPI.AddSurveyTermItem({ surveyId, sequence: 1, surveyTermItemId: 0 }),
				SurveyDefinitionsAPI.AddSurveyTermItem({ surveyId, sequence: 2, surveyTermItemId: 0 }),
			]);

			const surveyDefinitions = await SurveyDefinitionsAPI.FetchSurveyTermItems(surveyId, defaultCultureCode);

			// Set default survey definitions.
			// We will at least have one for title and one for non-title definition when it's enabled.
			const defaultSurveyDefinitions = surveyDefinitions.map((def) => {
				return {
					surveyTermItemId: def.surveyTermItemId,
					sequence: def.sequence,
					term: {
						textValue: '',
						editState: EditState.Add,
						language: defaultCultureCode,
						cultureCode: defaultCultureCode,
					},
					description: {
						textValue: '',
						editState: EditState.Add,
						language: defaultCultureCode,
						cultureCode: defaultCultureCode,
					},
				};
			});

			dispatch({
				type: ActionTypes.SURVEY_DEFINITIONS_SWITCH_UPDATE,
				payload: { checked: true, initialValue: defaultSurveyDefinitions },
			});
		} catch {
			dispatch({
				type: ActionTypes.SURVEY_DEFINITIONS_INITIALIZE_FAILED,
			});
		}
	},
	HandleToggleSurveyDefinitionsSwitch: (checked: boolean) => ({
		type: ActionTypes.SURVEY_DEFINITIONS_SWITCH_UPDATE,
		payload: { checked },
	}),
	HandleSurveyTermPageDescriptionChange: (value: string) => ({ type: ActionTypes.SURVEY_TERM_PAGE_DESCRIPTION_CHANGE, payload: value }),
	HandleTranslationEditorPageDescriptionUpdate: (item: ITranslationEditItem) => ({
		type: ActionTypes.SURVEY_TERM_PAGE_DESCRIPTION_UPDATE,
		payload: item,
	}),
	HandleTranslationEditorIntroUpdate: (item: ITranslationEditItem) => ({ type: ActionTypes.SURVEY_INTRO_UPDATED, payload: item }),
	HandleTranslationEditorInstructionsUpdate: (item: ITranslationEditItem) => ({
		type: ActionTypes.SURVEY_INSTRUCTIONS_UPDATED,
		payload: item,
	}),
	HandleTextToSpeechInstructionsUpdate: (item: ITranslationEditItem) => ({
		type: ActionTypes.SURVEY_TEXTTOSPEECH_INSTRUCTIONS_UPDATED,
		payload: item,
	}),
	HandleTextToSpeechInstructionsChange: (value: string) => ({ type: ActionTypes.SURVEY_TEXTTOSPEECH_INSTRUCTIONS_CHANGE, payload: value }),
	GetTextToSpeechSupportedCultures: () => (dispatch: any, getState: () => IApplicationState) => {
		const isTextToSpeechCulturesLoaded = getState().textToSpeechCultures.isTextToSpeechCulturesLoaded;
		const surveyEditModel = getState().survey.surveyEditModel;
		const isTextToSpeechEnabled = surveyEditModel.survey.isTextToSpeechEnabled;
		if (isTextToSpeechEnabled && !isTextToSpeechCulturesLoaded) {
			dispatch(TextToSpeechCultureStore.ActionCreators.GetTextToSpeechSupportedCulturesIfNeeded());
		}
	},
	UpdateSurveyDefaultCulture: (defaultCultureCode: string) => (dispatch: any, getState: () => IApplicationState) => {
		dispatch(ActionCreators.GetTextToSpeechSupportedCultures());
		const surveyEditModel = getState().survey.surveyEditModel;
		let supportedCultures = [...surveyEditModel.supportedCultures];
		const originalDefaultCultureCode = surveyEditModel.defaultCultureCode;
		if (!supportedCultures.includes(defaultCultureCode)) {
			supportedCultures = [defaultCultureCode].concat(supportedCultures);
		}
		const originalDefaultCultureCodeIndex = supportedCultures.indexOf(originalDefaultCultureCode);
		if (originalDefaultCultureCodeIndex > -1) {
			supportedCultures.splice(originalDefaultCultureCodeIndex, 1);
		}
		dispatch({ type: ActionTypes.CULTURES_DEFAULT_CULTURE_UPDATE, payload: { defaultCultureCode, supportedCultures } });
	},
	UpdateSelectedCultures: (selected: string[]) => (dispatch: any) => {
		dispatch(ActionCreators.GetTextToSpeechSupportedCultures());
		dispatch({ type: ActionTypes.CULTURES_SELECTION_UPDATE, payload: selected });
	},
	UpdateSelectedBenchmarks: (selected: number[]) => (dispatch: any) => {
		dispatch({ type: ActionTypes.BENCHMARKS_SELECTION_UPDATE, payload: selected });
		dispatch({ type: ActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_BENCHMARKMODAL_REMOVED });
	},
	AddBenchmark: (benchmarkId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.BENCHMARKS_SELECTION_ADD, payload: benchmarkId });
		dispatch({ type: ActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_BENCHMARKMODAL_ADDED });
	},
	RemoveBenchmark: (benchmarkId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.BENCHMARKS_SELECTION_REMOVE, payload: benchmarkId });
		dispatch({ type: ActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_BENCHMARKMODAL_REMOVED });
	},
	DeleteSurveyQuestion: (question: ISurveyQuestionEditModel) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEYQUESTION_DELETE, payload: question });
		if (question.sourceType === SourceType.Custom) {
			// for custom questions we cannot rely on the AttributeName always being populated
			dispatch({
				type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_REMOVE_SELECTED_BYCOLUMN,
				payload: question.externalAttributeColumnName,
			});
		} else {
			// for library questions we cannot rely on the ExternalAttributeColumnName always being populated
			dispatch({
				type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_REMOVE_SELECTED_BYNAME,
				payload: question.attributeName,
			});
		}
	},
	UpdateFollowUpQuestionMaxOption: (followupQuestionOptionMax: IFollowUpQuestionOptionMax) => (dispatch: any) => {
		dispatch({ type: ActionTypes.FOLLOWUPQUESTION_RESPONSELIMIT_UPDATE, payload: followupQuestionOptionMax });
	},
	UpdateFollowUpQuestionOptions: (followupQuestionOptions: IFollowUpQuestionOptions) => (dispatch: any) => {
		dispatch({ type: ActionTypes.FOLLOWUPQUESTION_RESPONSEOPTIONS_UPDATE, payload: followupQuestionOptions });
	},
	UpdateAllQuestionTypes: (questions: ISurveyQuestionEditModel[], questionType: QuestionType) => (dispatch: any) => {
		dispatch({ type: ActionTypes.UPDATE_QUESTION_TYPE, payload: { questions, questionType } });
	},
	ToggleAllQuestionsAreRequired: (isRequired: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.UPDATE_QUESTION_REQUIRED, payload: isRequired });
	},
	SortSurveyQuestions: (elementIds: number[], isFirstPageRandomized: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEYQUESTION_LIST_SORT, payload: { ids: elementIds, isFirstPageRandomized: isFirstPageRandomized } });
	},
	ToggleQuestionIsRequired: (question: ISurveyQuestionEditModel, isRequired: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.QUESTION_IS_REQUIRED_TOGGLE, payload: { isRequired, question } });
	},
	HandleSurveyProgressMeterTypeChange: (selectedValue: ProgressMeterType) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_PROGRESS_METER_TYPE_CHANGE, payload: selectedValue });
	},
	HandleShowQuestionNumberingChange: (selectedValue: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SHOW_QUESTION_NUMBERING, payload: selectedValue });
	},
	SetRandomizeItemsInSection: (sequenceStart: number, shouldRandomize: boolean) => (dispatch: any, getState: () => IApplicationState) => {
		const nextPageBreak = getState().survey.surveyEditModel.pageFlowElements.filter(
			(e) => e.sequence && e.sequence > sequenceStart && e.elementType == ElementType.PageBreak,
		);
		const sequenceEnd =
			nextPageBreak.length > 0
				? nextPageBreak[0].sequence
				: getState().survey.surveyEditModel.questions.length + getState().survey.surveyEditModel.pageFlowElements.length + 1;
		dispatch({
			type: ActionTypes.SET_RANDOMIZE_ON_QUESTIONS_IN_SEQUENCE,
			payload: { sequenceStart: sequenceStart, sequenceEnd: sequenceEnd, shouldRandomize: shouldRandomize },
		});
	},
	HandleShowKincentricLogoChange: (showKincentricLogo: boolean) => (dispatch: AppDispatch) => {
		dispatch({ type: ActionTypes.SHOW_KINCENTRIC_LOGO_CHANGE, payload: showKincentricLogo });
	},
	LoadSurveyDimensions: (surveyId: number) => (dispatch: AppDispatch) => {
		dispatch(loadSurveyDimensions(surveyId));
	},
	SubmitPageFlowElementModal: (pageFlowElements: IPageFlowElementModel[]) => (dispatch: AppDispatch) => {
		dispatch({
			type: ActionTypes.PAGEFLOW_ELEMENT_MODAL_SUBMIT,
			payload: {
				pageFlowElements,
			},
		});
	},
	DeleteSurveyElement: (id: number) => (dispatch: AppDispatch) => {
		dispatch({ type: ActionTypes.SURVEYELEMENT_DELETE, payload: { id } });
	},
	UpdateSurveyQuestionDisplayRulesCount:
		(surveyQuestionId: number, displayRulesCount: number, operation: 'add' | 'set') => (dispatch: AppDispatch) => {
			dispatch({
				type: ActionTypes.SURVEY_QUESTION_DISPLAY_RULES_COUNT_UPDATE,
				payload: {
					surveyQuestionId,
					displayRulesCount,
					operation,
				},
			});
		},
	HandlePublishDestination: (destination: PublishDestination) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_PUBLISH_DESTINATION_CHANGE, payload: destination });
	},
};

type KnownActions =
	| Actions.ISurveySetDefaultState
	| Actions.ISurveyGetRequested
	| Actions.ISurveyGetSucceeded
	| Actions.ISurveyGetFailed
	| Actions.ISurveySaveSucceeded
	| Actions.ISurveyNameUpdate
	| Actions.ISurveyTextToSpeechUpdate
	| Actions.ISurveySetAudienceSourceType
	| Actions.ISurveySetExternalSource
	| Actions.ISurveyTitleUpdated
	| Actions.ISurveyTitleChange
	| Actions.ISurveyIntroUpdated
	| Actions.ISurveyIntroChange
	| Actions.ISurveyContentItemSort
	| Actions.ISurveyInstructionsUpdated
	| Actions.ISurveyInstructionsChange
	| Actions.ISurveyTextToSpeechInstructionsUpdated
	| Actions.ISurveyTextToSpeechInstructionsChange
	| Actions.ICultureUpdateSelected
	| Actions.ICultureUpdateSurveyDefaultCulture
	| Actions.IBenchmarksUpdateSelected
	| Actions.IBenchmarkAdd
	| Actions.IBenchmarkRemove
	| Actions.IBenchmarkGlobalTopQuartileId
	| Actions.IDeleteSurveyQuestion
	| Actions.IUpdateFollowUpQuestionMaxOption
	| Actions.IUpdateFollowUpQuestionOptions
	| Actions.IUpdateAllQuestionTypes
	| Actions.IToggleAllQuestionsAreRequired
	| Actions.ISurveyQuestionListSort
	| Actions.ISurveyQuestionsModalSubmit
	| Actions.IStepperRoutingDiscardChanges
	| Actions.ISurveyBenchmarksGetSucceeded
	| Actions.ICustomQuestionSaveSucceeded
	| Actions.IQuestionIsRequiredToggle
	| Actions.IHideQuestionToggle
	| Actions.IExternalApplicationTypesGetRequested
	| Actions.IExternalApplicationTypesGetSucceeded
	| Actions.IExternalApplicationTypesGetFailed
	| Actions.ISurveySetExternalApplicationType
	| Actions.ISurveySetSurveyType
	| Actions.ISurveyQuestionDisplayRulesCountUpdate
	| Actions.ISurveyQuestionSyncWithQuestionLibrary
	| Actions.ISurveyQuestionsSyncWithQuestionLibrary
	| Actions.IPageFlowElementModalSubmit
	| Actions.ISurveyElementDelete
	| Actions.IShowKincentricLogoChange
	| Actions.ISurveyProgressMeterTypeChange
	| Actions.ISetRandomizeOnSurveyQuestionsInSequence
	| Actions.IShowQuestionNumberingChange
	| Actions.IPublishDestinationChange
	| Actions.ISurveyPrivacyPolicyCopyUpdate
	| Actions.ISurveyPrivacyPolicyClearUpdate
	| Actions.ISurveyPrivacyPolicySwitchUpdate
	| Actions.ISurveyPrivacyPolicyTitleChange
	| Actions.ISurveyPrivacyPolicyContentChange
	| Actions.ISurveyPrivacyPolicyCheckBoxChange
	| Actions.ISurveyPrivacyPolicyAdminSaved
	| Actions.ISurveyPrivacyPolicyTranslationUpdated
	| Actions.ISurveyDefinitionsSwitchUpdate
	| Actions.ISurveyDefinitionsInitializeRequested
	| Actions.ISurveyDefinitionsInitializeFailed
	| Actions.ISurveyTermPageDescriptionChange
	| Actions.ISurveyTermPageDescriptionUpdate
	| Actions.ISurveyTermItemsFetch
	| Actions.ISurveyTermItemAdd
	| Actions.ISurveyTermItemDelete
	| Actions.ISurveyTermItemChange
	| Actions.ISurveyTermAdminLoading
	| Actions.ISurveyTermAdminSaved
	| Actions.ISurveyTermTranslationUpdated;

export const reducer: Reducer<ISurveyState, KnownActions> = (state: ISurveyState = defaultState, action: KnownActions): ISurveyState => {
	switch (action.type) {
		case ActionTypes.SURVEY_SET_DEFAULT_STATE:
			if (state.applicationTypes.length > 0) {
				return { ...defaultState, applicationTypes: state.applicationTypes };
			}
			return { ...defaultState };
		case ActionTypes.BENCHMARK_GET_GLOBALTOPQUARTILEID:
			if (state.surveyEditModel.benchmarks.find((id: number) => id === action.payload) === undefined) {
				return {
					...state,
					surveyEditModel: {
						...state.surveyEditModel,
						benchmarks: [action.payload, ...state.surveyEditModel.benchmarks],
					},
					lastSavedSurveyEditModel: {
						...state.lastSavedSurveyEditModel,
						benchmarks: [action.payload, ...state.lastSavedSurveyEditModel.benchmarks],
					},
				};
			}
			break;
		// GET
		case ActionTypes.SURVEY_GET_REQUESTED:
		case ActionTypes.EXTERNAL_APPLICATION_TYPES_GET_REQUESTED:
			return { ...state, isLoading: true };
		case ActionTypes.SURVEY_QUESTION_DISPLAY_RULES_COUNT_UPDATE:
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions: state.surveyEditModel.questions.map((q) =>
						q.surveyQuestionId === action.payload.surveyQuestionId
							? {
									...q,
									displayRulesCount: {
										add: q.displayRulesCount + action.payload.displayRulesCount,
										set: action.payload.displayRulesCount,
									}[action.payload.operation],
							  }
							: { ...q },
					),
				},
			};
		case ActionTypes.SURVEY_QUESTION_SYNC_WITH_QUESTIONLIBRARY: {
			const surveyQuestion = action.payload;
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions: state.surveyEditModel.questions.map((q) => {
						return q.surveyQuestionId === surveyQuestion.surveyQuestionId
							? {
									...q,
									text: surveyQuestion.text,
									responseOptions: surveyQuestion.responseOptions,
									responseOptionGroup: surveyQuestion.responseOptionGroup,
									syncWithQuestionLibrary: true,
							  }
							: { ...q };
					}),
				},
			};
		}
		case ActionTypes.SURVEY_QUESTIONS_SYNC_WITH_QUESTIONLIBRARY: {
			const questionChanges = action.payload;
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions: state.surveyEditModel.questions.map((q) => {
						const questionChange = _.find(questionChanges, (qc) => qc.surveyQuestionId === q.surveyQuestionId);
						return questionChange && questionChange.question
							? {
									...q,
									text: questionChange.question.text,
									responseOptionGroup: questionChange.question.responseOptionGroup,
									responseOptions: questionChange.question.responseOptions,
									syncWithQuestionLibrary: true,
							  }
							: { ...q };
					}),
				},
			};
		}

		case ActionTypes.SURVEY_GET_FAILED:
			return {
				...state,
				isLoading: false,
				surveyEditModel: { ...state.surveyEditModel, surveyTerm: { ...state.surveyEditModel.surveyTerm, isLoading: false } },
			};

		case ActionTypes.EXTERNAL_APPLICATION_TYPES_GET_FAILED:
			return { ...state, isLoading: false };

		case ActionTypes.SURVEY_GET_SUCCEEDED: {
			let surveyEditModel = action.payload.subject;
			let initialQuestions = surveyEditModel.questions.map((q, index) => ({ ...q, id: index + 1 }));
			// When adding questions, newly added questions use the id property instead questionId until Save.
			// This foreach remaps the database questionId to id for consistent UI behavior.
			initialQuestions = initialQuestions.map((sq: ISurveyQuestionEditModel) => {
				if (sq.parentQuestionId) {
					return { ...sq, parentQuestionId: initialQuestions.find((q) => q.surveyQuestionId === sq.parentQuestionId)?.id };
				} else {
					return sq;
				}
			});
			const numQuestions = initialQuestions.length;
			const initialPageFlowElements = surveyEditModel.pageFlowElements.map((p, index) => ({ ...p, id: numQuestions + index + 1 }));
			const defaultTranslationEditItem = InitDefaultTranslationEditItem(state.surveyEditModel.defaultCultureCode);

			surveyEditModel = {
				...surveyEditModel,
				questions: initialQuestions,
				pageFlowElements: initialPageFlowElements,
				title: surveyEditModel.title || { ...defaultTranslationEditItem },
				intro: surveyEditModel.intro || { ...defaultTranslationEditItem },
				instructions: surveyEditModel.instructions || { ...defaultTranslationEditItem },
				textToSpeechInstructions: surveyEditModel.textToSpeechInstructions || { ...defaultTranslationEditItem },
			};
			return {
				...state,
				isLoading: false,
				surveyEditModel,
				lastSavedSurveyEditModel: { ...surveyEditModel },
			};
		}

		case ActionTypes.EXTERNAL_APPLICATION_TYPES_GET_SUCCEEDED: {
			const toolTipString = <Tooltip id={'application-disabled'}>{ContentConstants.EXTERNAL_APPLICATION_TYPE_DISABLED}</Tooltip>;
			const options = _.map(action.payload, (appTypeVm: IExternalApplicationTypeVm) => {
				return {
					name: appTypeVm.externalApplicationType.toString(),
					displayText: (
						<DisplayTextWithDetails
							displayText={appTypeVm.displayName}
							logo={appTypeVm.logo}
							details={appTypeVm.isDisabled ? toolTipString : ''}
						/>
					),
					disabled: appTypeVm.isDisabled,
				};
			});

			return {
				...state,
				isLoading: false,
				applicationTypes: options,
			};
		}

		case ActionTypes.BENCHMARKS_SELECTION_UPDATE:
		case ActionTypes.SURVEY_BENCHMARKS_GET_SUCCEEDED:
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					benchmarks: action.payload,
				},
			};

		// UPDATES
		case ActionTypes.SURVEY_NAME_UPDATE:
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					survey: {
						...state.surveyEditModel.survey,
						name: action.payload,
					},
				},
			};
		case ActionTypes.SURVEY_TEXTTOSPEECH_UPDATE:
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					survey: {
						...state.surveyEditModel.survey,
						isTextToSpeechEnabled: action.payload,
					},
				},
			};
		case ActionTypes.SURVEY_SET_AUDIENCE_SOURCE_TYPE: {
			const audienceSource: IAudienceSourceVm = { ...state.surveyEditModel.audienceSource };
			if (audienceSource.audienceSourceType !== action.payload) {
				audienceSource.audienceSourceType = action.payload;
				audienceSource.externalHrisId = undefined;
				audienceSource.externalHrisFileName = undefined;
				audienceSource.externalHierarchyId = undefined;
			}
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					audienceSource,
				},
			};
		}
		case ActionTypes.SURVEY_SET_EXTERNAL_SOURCE:
			// NOTE: when we add support for setting single hierarchy, setting the payload
			// 	will be dependent on the audience source
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					audienceSource: {
						...state.surveyEditModel.audienceSource,
						externalHrisId: action.payload.externalHrisId,
						externalHierarchyId: action.payload.externalHierarchyId,
					},
				},
			};
		case ActionTypes.SURVEY_SET_EXTERNAL_APPLICATION_TYPE:
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					survey: {
						...state.surveyEditModel.survey,
						externalApplicationType: action.payload,
					},
				},
			};
		case ActionTypes.SURVEY_SET_SURVEY_TYPE:
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					survey: {
						...state.surveyEditModel.survey,
						surveyTypes: action.payload,
					},
				},
			};
		case ActionTypes.SURVEY_TITLE_UPDATED:
			return {
				...state,
				lastSavedSurveyEditModel: {
					...state.lastSavedSurveyEditModel,
					title: {
						...action.payload,
						editState: EditState.None,
					},
				},
				surveyEditModel: {
					...state.surveyEditModel,
					title: {
						...action.payload,
						editState: EditState.None,
					},
				},
			};
		case ActionTypes.SURVEY_TITLE_CHANGE: {
			const originalTitle: ITranslationEditItem = state.lastSavedSurveyEditModel.title
				? { ...state.lastSavedSurveyEditModel.title }
				: { ...InitDefaultTranslationEditItem(state.surveyEditModel.defaultCultureCode) };
			const updatedTitle = { ...originalTitle, textValue: action.payload };
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					title: {
						...updatedTitle,
						editState: TranslationHelper.getEditState(updatedTitle, originalTitle),
					},
				},
			};
		}
		case ActionTypes.SURVEY_INTRO_UPDATED:
			return {
				...state,
				lastSavedSurveyEditModel: {
					...state.lastSavedSurveyEditModel,
					intro: {
						...action.payload,
						editState: EditState.None,
					},
				},
				surveyEditModel: {
					...state.surveyEditModel,
					intro: {
						...action.payload,
						editState: EditState.None,
					},
				},
			};
		case ActionTypes.SURVEY_INTRO_CHANGE: {
			const originalIntro: ITranslationEditItem = state.lastSavedSurveyEditModel.intro
				? { ...state.lastSavedSurveyEditModel.intro }
				: { ...InitDefaultTranslationEditItem(state.surveyEditModel.defaultCultureCode) };
			const updatedIntro = { ...originalIntro, textValue: action.payload };
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					intro: {
						...updatedIntro,
						editState: TranslationHelper.getEditState(updatedIntro, originalIntro),
					},
				},
			};
		}
		case ActionTypes.SURVEY_CONTENT_ITEM_SORT: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					survey: {
						...state.surveyEditModel.survey,
						contentItemsOrder: action.payload,
					},
				},
			};
		}
		case ActionTypes.SURVEY_INSTRUCTIONS_UPDATED:
			return {
				...state,
				lastSavedSurveyEditModel: {
					...state.lastSavedSurveyEditModel,
					instructions: {
						...action.payload,
						editState: EditState.None,
					},
				},
				surveyEditModel: {
					...state.surveyEditModel,
					instructions: {
						...action.payload,
						editState: EditState.None,
					},
				},
			};
		case ActionTypes.SURVEY_INSTRUCTIONS_CHANGE: {
			const originalInstructions: ITranslationEditItem = state.lastSavedSurveyEditModel.instructions
				? { ...state.lastSavedSurveyEditModel.instructions }
				: { ...InitDefaultTranslationEditItem(state.surveyEditModel.defaultCultureCode) };
			const updatedInstructions = { ...originalInstructions, textValue: action.payload };
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					instructions: {
						...updatedInstructions,
						editState: TranslationHelper.getEditState(updatedInstructions, originalInstructions),
					},
				},
			};
		}
		case ActionTypes.SURVEY_TEXTTOSPEECH_INSTRUCTIONS_UPDATED:
			return {
				...state,
				lastSavedSurveyEditModel: {
					...state.lastSavedSurveyEditModel,
					textToSpeechInstructions: {
						...action.payload,
						editState: EditState.None,
					},
				},
				surveyEditModel: {
					...state.surveyEditModel,
					textToSpeechInstructions: {
						...action.payload,
						editState: EditState.None,
					},
				},
			};
		case ActionTypes.SURVEY_TEXTTOSPEECH_INSTRUCTIONS_CHANGE: {
			const originalInstructions: ITranslationEditItem = state.lastSavedSurveyEditModel.textToSpeechInstructions
				? { ...state.lastSavedSurveyEditModel.textToSpeechInstructions }
				: { ...InitDefaultTranslationEditItem(state.surveyEditModel.defaultCultureCode) };
			const updatedInstructions = { ...originalInstructions, textValue: action.payload };
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					textToSpeechInstructions: {
						...updatedInstructions,
						editState: TranslationHelper.getEditState(updatedInstructions, originalInstructions),
					},
				},
			};
		}
		case ActionTypes.CULTURES_SELECTION_UPDATE:
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					supportedCultures: action.payload,
				},
			};
		case ActionTypes.CULTURES_DEFAULT_CULTURE_UPDATE:
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					defaultCultureCode: action.payload.defaultCultureCode,
					supportedCultures: action.payload.supportedCultures,
				},
			};
		case ActionTypes.BENCHMARKS_SELECTION_ADD: {
			const withAddedbenchmark = [...state.surveyEditModel.benchmarks];
			withAddedbenchmark.push(action.payload);
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					benchmarks: withAddedbenchmark,
				},
			};
		}
		case ActionTypes.BENCHMARKS_SELECTION_REMOVE: {
			const withRemovedbenchmark = _.reject(state.surveyEditModel.benchmarks, (id: number) => id === action.payload);
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					benchmarks: withRemovedbenchmark,
				},
			};
		}
		case ActionTypes.CUSTOM_QUESTION_SAVE_SUCCEEDED: {
			const customQuestion = { ...action.payload.question };

			if (customQuestion.isActive) {
				const { defaultCultureCode, questions } = state.surveyEditModel;

				const newTextValue = TranslationHelper.getTranslationByCultureCode(defaultCultureCode, customQuestion.translations);
				const newQuestions = [...questions];
				const index = _.findIndex(newQuestions, (q) => q.surveyQuestionId === customQuestion.surveyQuestionId);
				const responseOptions = customQuestion.responseOptions?.map((ro) => {
					return _.find(ro.translations, (t) => t.cultureCode === defaultCultureCode)?.textValue ?? '';
				});
				if (index > -1) {
					let question = newQuestions[index];
					question = {
						...question,
						text: newTextValue,
						questionType: customQuestion.questionType,
						responseOptionGroup: customQuestion.responseOptionGroupName,
						responseOptions,
						dimensionNames: customQuestion.dimensions.map((d) => d.name),
						parentQuestionId: action.payload.parentQuestionId,
					};
					newQuestions.splice(index, 1, question);
				}
				return {
					...state,
					surveyEditModel: {
						...state.surveyEditModel,
						questions: newQuestions,
					},
				};
			}
			return { ...state };
		}

		// SAVING
		case ActionTypes.STEPPER_ROUTING_DISCARD_CHANGES:
			return {
				...state,
				surveyEditModel: { ...state.lastSavedSurveyEditModel },
			};

		case ActionTypes.SURVEY_SAVE_SUCCEEDED: {
			let savedEditModel = action.payload;
			let savedQuestions = savedEditModel.questions.map((q, index) => ({ ...q, id: index + 1 }));
			// When adding questions, newly added questions use the id property instead questionId until Save.
			// This remaps the database questionId to id for consistent UI behavior.
			savedQuestions = savedQuestions.map((sq: ISurveyQuestionEditModel) => {
				if (sq.parentQuestionId) {
					return { ...sq, parentQuestionId: savedQuestions.find((q) => q.surveyQuestionId === sq.parentQuestionId)?.id };
				} else {
					return sq;
				}
			});
			const numQuestions = savedQuestions.length;
			const savedPageFlowElements = savedEditModel.pageFlowElements.map((p, index) => ({ ...p, id: numQuestions + index + 1 }));
			const defaultTranslationEditItem = InitDefaultTranslationEditItem(state.surveyEditModel.defaultCultureCode);

			savedEditModel = {
				...savedEditModel,
				questions: savedQuestions,
				pageFlowElements: savedPageFlowElements,
				title: savedEditModel.title || { ...defaultTranslationEditItem },
				intro: savedEditModel.intro || { ...defaultTranslationEditItem },
				instructions: savedEditModel.instructions || { ...defaultTranslationEditItem },
				textToSpeechInstructions: savedEditModel.textToSpeechInstructions || { ...defaultTranslationEditItem },
			};
			return {
				...state,
				isLoading: false,
				surveyEditModel: savedEditModel,
				lastSavedSurveyEditModel: { ...savedEditModel },
			};
		}
		// DELETE
		case ActionTypes.SURVEYQUESTION_DELETE: {
			// When a question is removed from the list in the UI, it needs to be flagged as removed
			// unless it is a custom question. For now, Custom Questions are merely inactivated.
			const { surveyQuestionId, sourceQuestionVariationId } = action.payload;
			let questions = state.surveyEditModel.questions;
			if (!surveyQuestionId) {
				// This question was never saved to the DB
				questions = questions.filter((q: ISurveyQuestionEditModel) => q.sourceQuestionVariationId !== sourceQuestionVariationId);
				// Re-index when removing so newly added follow-up questions can be correctly parented.
				questions = questions.map((q, index) => {
					const newId = index + 1;
					const followUpQuestion = questions.find((fq) => fq.parentQuestionId && fq.parentQuestionId === q.id);
					if (followUpQuestion) {
						followUpQuestion.parentQuestionId = newId;
					}
					return { ...q, id: newId };
				});
			} else {
				questions = questions.map((q: ISurveyQuestionEditModel) => {
					switch (q.sourceType) {
						case SourceType.Library:
							if (q.sourceQuestionVariationId === sourceQuestionVariationId) {
								return { ...q, editState: EditState.Remove };
							}
							break;
						case SourceType.Custom:
							if (q.surveyQuestionId === surveyQuestionId) {
								return { ...q, editState: EditState.Update, isActive: false };
							}
							break;
					}
					return { ...q };
				});
			}
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions,
				},
			};
		}
		// FollowUp Question max option value update
		case ActionTypes.FOLLOWUPQUESTION_RESPONSELIMIT_UPDATE: {
			const { value, question } = action.payload;
			const newQuestions = state.surveyEditModel.questions.map((q) => {
				return {
					...q,
					selectionLimit: q.id === question.id ? value : q.selectionLimit,
				};
			});
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions: newQuestions,
				},
			};
		}
		// FollowUp Question max option value update
		case ActionTypes.FOLLOWUPQUESTION_RESPONSEOPTIONS_UPDATE: {
			const { values, question } = action.payload;
			const sortedValues = [...values].sort((a, b) => a.localeCompare(b));
			const newQuestions = state.surveyEditModel.questions.map((q) => {
				return {
					...q,
					responseOptions: q.id === question.id ? sortedValues : q.responseOptions,
				};
			});
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions: newQuestions,
				},
			};
		}
		// UPDATE QUESTION TYPE
		case ActionTypes.UPDATE_QUESTION_TYPE: {
			const { questions } = action.payload;
			const questionType = Number(action.payload.questionType);
			let surveyQuestions = state.surveyEditModel.questions;
			surveyQuestions = surveyQuestions.map((sq: ISurveyQuestionEditModel) => {
				return (sq.questionType === QuestionType.Scale || sq.questionType === QuestionType.Slider) &&
					_.some(questions, (q: ISurveyQuestionEditModel) =>
						q.sourceQuestionVariationId
							? q.sourceQuestionVariationId === sq.sourceQuestionVariationId
							: q.surveyQuestionId === sq.surveyQuestionId,
					)
					? { ...sq, questionType }
					: { ...sq };
			});

			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions: surveyQuestions,
				},
			};
		}
		case ActionTypes.UPDATE_QUESTION_REQUIRED: {
			const isRequired = action.payload;
			let surveyQuestions = state.surveyEditModel.questions;
			surveyQuestions = surveyQuestions.map((sq: ISurveyQuestionEditModel) => {
				return !sq.isDemographic ? { ...sq, isRequired } : { ...sq };
			});

			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions: surveyQuestions,
				},
			};
		}
		// RE-ORDERING
		case ActionTypes.SURVEYQUESTION_LIST_SORT: {
			const questions = _.cloneDeep(state.surveyEditModel.questions);
			const followupQuestions = questions.filter((q) => q.parentQuestionId);
			const elements = _.cloneDeep(state.surveyEditModel.pageFlowElements as ISurveyElement[]).concat(questions);
			const sortedElements: ISurveyElement[] = [];

			let displayOrder = 1;
			let sequenceAdd = 1;
			let isRandom = action.payload.isFirstPageRandomized;
			action.payload.ids
				.filter((id) => !followupQuestions.some((q) => q.id === id))
				.forEach((id, index) => {
					const element = elements.find((e) => e.id === id)!;
					element.sequence = index + sequenceAdd;
					if (element.elementType === ElementType.Question) {
						(element as ISurveyQuestionEditModel).displayOrder = displayOrder;
						displayOrder += 1;
					}
					if (element.elementType === ElementType.PageBreak) {
						isRandom = element.isRandomized || false;
					}
					sortedElements.push({ ...element, isRandomized: isRandom });
					//attach followup questions if exists
					const followUpQuestion = followupQuestions.find((q) => q.parentQuestionId === id);
					if (followUpQuestion) {
						followUpQuestion.displayOrder = displayOrder;
						sequenceAdd += 1;
						displayOrder += 1;
						followUpQuestion.sequence = index + sequenceAdd;
						sortedElements.push({ ...followUpQuestion, isRandomized: isRandom });
					}
				});

			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions: sortedElements.filter((e) => e.elementType === ElementType.Question).map((q) => q as ISurveyQuestionEditModel),
					pageFlowElements: sortedElements.filter(
						(e) =>
							e.elementType === ElementType.PageBreak ||
							e.elementType === ElementType.SectionBreak ||
							e.elementType === ElementType.SectionHeader ||
							e.elementType === ElementType.TextElement,
					) as IPageFlowElementModel[],
				},
			};
		}

		// COMMITTING QUESTIONS FROM MODAL
		case ActionTypes.SURVEYQUESTIONS_MODAL_SUBMIT: {
			// - addedQuestions will only have questions selected (not removed)
			// - click question to remove, then click same question to add:
			//     addedQuestions will have that question, even though there is no real change
			// - selectedQuestionVariationIds + selectedCustomQuestionIds will hold all questions the modal knows about,
			//   and any removed question will no longer be in one of these collections
			// - but, those collections will only hold question ids for the question types handled by the
			//   current instance of the modal (1,2 for questions, 3 for demographic questions)
			const { addedQuestions, isDemographic, selectedQuestionVariationIds, selectedCustomQuestionIds } = action.payload;

			const isReAddedQuestion = (existingQuestion: ISurveyQuestionEditModel, addedQuestion: IAddedQuestion) => {
				if (addedQuestion.sourceType === SourceType.Custom) {
					if (existingQuestion.surveyQuestionId === addedQuestion.questionId && existingQuestion.isActive === false) {
						return true;
					}
				} else if (QuestionEqualityHelper.equals(addedQuestion, existingQuestion) && existingQuestion.editState === EditState.Remove) {
					return true;
				}

				return false;
			};

			// Flip status from remove to update for questions which was added again in a modal.
			const existingQuestions: ISurveyQuestionEditModel[] = state.surveyEditModel.questions.map((e) => {
				const reAddedQuestionIndex = addedQuestions.findIndex((q) => isReAddedQuestion(e, q));
				if (reAddedQuestionIndex !== -1) {
					return {
						...e,
						isActive: true,
						editState: EditState.Update,
						parentQuestionId: addedQuestions.find((q) => q.questionId === e.surveyQuestionId)?.parentQuestionId,
					};
				}
				return { ...e };
			});

			// identify what, if any, questions in addedQuestions are new to the survey
			const newQuestions = addedQuestions.filter((q) => existingQuestions.findIndex((e) => QuestionEqualityHelper.equals(q, e)) === -1);

			// NOTE: if the user removes a question from the list on the main define page, then opens
			// the modal and re-adds that same question, we will miss that in the newQuestions collection
			// above. We would have to identify that as an updated question and sync the edit model
			// questions (i.e. un-remove it).

			// identify what questions may have been removed
			const removedLibraryQuestions = existingQuestions
				.filter((e) => e.sourceType === SourceType.Library)
				.filter((e) => e.isDemographic === isDemographic)
				.filter((e) => selectedQuestionVariationIds.indexOf(e.sourceQuestionVariationId!) === -1);
			const removedCustomQuestions = existingQuestions
				.filter((e) => e.sourceType === SourceType.Custom)
				.filter((e) => e.isDemographic === isDemographic)
				.filter((e) => selectedCustomQuestionIds.indexOf(e.surveyQuestionId!) === -1)
				.map((e) => ({ ...e, isActive: false }));

			const removedQuestions = removedLibraryQuestions.concat(removedCustomQuestions).map((e) => {
				return { ...e, editState: EditState.Remove };
			});

			// map new questions (AddedQuestion model) to SurveyQuestionEditModel
			const incomingQuestions = newQuestions.map((addedQ: IAddedQuestion) => {
				const newQuestion = new SurveyQuestionEditModel(addedQ);
				if ((newQuestion.sourceType as number) === (CommonSourceType.Custom as number)) {
					// a custom question never gets "created" here, it'll only be activated
					newQuestion.editState = EditState.Update;
					newQuestion.isActive = true;
					newQuestion.surveyQuestionId = addedQ.questionId;
				}
				return newQuestion;
			});

			let allQuestions = existingQuestions
				.filter((e) => removedQuestions.findIndex((r) => QuestionEqualityHelper.equals(e, r)) === -1)
				.concat(removedQuestions);
			_.forEach(incomingQuestions, (question) => {
				const surveyQuestionEditModel: ISurveyQuestionEditModel = { ...question };
				if (question.parentQuestionId) {
					// Insert mid-collection at the next spot following the parent question.
					const index = allQuestions.findIndex((q) => q.id === question.parentQuestionId);
					allQuestions.splice(index + 1, 0, surveyQuestionEditModel);
				} else {
					// Add other questions to the end of the collection.
					allQuestions.push(surveyQuestionEditModel);
				}
			});
			allQuestions = allQuestions.map((q, index) => {
				const newId = index + 1;
				const followUpQuestion = allQuestions.find(
					// There can be a brief moment where the follow-up question hasn't received its new id and may appear assigned to itself.
					(fq) => fq.parentQuestionId && fq.parentQuestionId === q.id && q.id !== q.parentQuestionId,
				);
				if (followUpQuestion) {
					followUpQuestion.parentQuestionId = newId;
				}
				return { ...q, id: newId };
			});
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions: allQuestions,
				},
			};
		}

		// COMMITTING SURVEY ELEMENT FROM MODAL
		case ActionTypes.PAGEFLOW_ELEMENT_MODAL_SUBMIT: {
			const { pageFlowElements } = action.payload;

			const addedPagebreakIds = action.payload.pageFlowElements.filter((e) => e.elementType === ElementType.PageBreak).map((pe) => pe.id);
			let elements: ISurveyElement[] = (state.surveyEditModel.pageFlowElements as ISurveyElement[]).concat(state.surveyEditModel.questions);
			elements = SurveyDesignHelper.ResequenceElements(elements, pageFlowElements);

			if (addedPagebreakIds.length > 0) {
				const isFirstPageRandomized = state.surveyEditModel.questions.length > 0 ? state.surveyEditModel.questions[0]?.isRandomized : false;
				elements = SurveyDesignHelper.SetRandomizationForNewPageBreaks(elements, addedPagebreakIds, isFirstPageRandomized || false);
			}

			const updatedQuestions = elements.filter((q: ISurveyElement) => {
				return q.elementType === ElementType.Question;
			}) as ISurveyQuestionEditModel[];
			const updatedPageFlowElements = elements.filter((e: ISurveyElement) => {
				return SurveyDesignHelper.IsPageFlowElement(e);
			}) as IPageFlowElementModel[];

			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions: updatedQuestions,
					pageFlowElements: updatedPageFlowElements,
				},
			};
		}

		// Delete Survey Element
		case ActionTypes.SURVEYELEMENT_DELETE: {
			const { id } = action.payload;

			const questions = state.surveyEditModel.questions;
			const elements = (state.surveyEditModel.pageFlowElements as ISurveyElement[]).concat(questions);

			const index = elements.findIndex((e) => e.id === id);
			const deletedSequence = elements[index].sequence;
			elements.splice(index, 1);

			const updatedElements = elements.map((e) => {
				if (e.sequence! >= deletedSequence!) {
					return { ...e, sequence: e.sequence! - 1 };
				} else {
					return { ...e };
				}
			});

			const updatedQuestions = updatedElements.filter((q: ISurveyElement) => {
				return q.elementType === ElementType.Question;
			}) as ISurveyQuestionEditModel[];
			const updatedPageFlowElements = updatedElements.filter((e: ISurveyElement) => {
				return SurveyDesignHelper.IsPageFlowElement(e);
			}) as IPageFlowElementModel[];

			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					questions: updatedQuestions,
					pageFlowElements: updatedPageFlowElements,
				},
			};
		}

		case ActionTypes.QUESTION_IS_REQUIRED_TOGGLE: {
			const newQuestion: ISurveyQuestionEditModel = {
				...action.payload.question,
				isRequired: action.payload.isRequired,
				isHidden: action.payload.isRequired ? false : action.payload.question.isHidden,
				editState: action.payload.question.editState === EditState.None ? EditState.Update : action.payload.question.editState,
			};
			const newQuestions = state.surveyEditModel.questions.map((q: ISurveyQuestionEditModel) =>
				q.id === newQuestion.id ? newQuestion : q,
			);
			return { ...state, surveyEditModel: { ...state.surveyEditModel, questions: newQuestions } };
		}
		case ActionTypes.HIDE_QUESTION_TOGGLE: {
			const newQuestion: ISurveyQuestionEditModel = {
				...action.payload.question,
				isHidden: action.payload.isHidden,
				editState: action.payload.question.editState === EditState.None ? EditState.Update : action.payload.question.editState,
			};
			const newQuestions = state.surveyEditModel.questions.map((q: ISurveyQuestionEditModel) =>
				q.id === newQuestion.id ? newQuestion : q,
			);
			return { ...state, surveyEditModel: { ...state.surveyEditModel, questions: newQuestions } };
		}
		case ActionTypes.SHOW_KINCENTRIC_LOGO_CHANGE: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					survey: {
						...state.surveyEditModel.survey,
						showKincentricLogo: action.payload,
					},
				},
			};
		}
		case ActionTypes.SURVEY_PROGRESS_METER_TYPE_CHANGE: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					progressMeterType: action.payload,
					survey: {
						...state.surveyEditModel.survey,
						progressMeterType: action.payload,
					},
				},
			};
		}

		case ActionTypes.SHOW_QUESTION_NUMBERING: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					shouldShowQuestionNumbering: action.payload,
					survey: {
						...state.surveyEditModel.survey,
						shouldShowQuestionNumbering: action.payload,
					},
				},
			};
		}

		case ActionTypes.SURVEY_PUBLISH_DESTINATION_CHANGE: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					survey: {
						...state.surveyEditModel.survey,
						publishDestination: action.payload,
					},
				},
			};
		}

		case ActionTypes.SET_RANDOMIZE_ON_QUESTIONS_IN_SEQUENCE: {
			const currentQuestions = state.surveyEditModel.questions.slice(0);
			const updatedQuestions = currentQuestions.map((q) => ({
				...q,
				isRandomized:
					q.sequence && q.sequence >= action.payload.sequenceStart && q.sequence < action.payload.sequenceEnd
						? action.payload.shouldRandomize
						: q.isRandomized,
			}));
			const updatedPageFlowElements = state.surveyEditModel.pageFlowElements.map((e) => ({
				...e,
				isRandomized: e.sequence === action.payload.sequenceStart ? action.payload.shouldRandomize : e.isRandomized,
			}));
			return {
				...state,
				surveyEditModel: { ...state.surveyEditModel, questions: updatedQuestions, pageFlowElements: updatedPageFlowElements },
			};
		}
		case ActionTypes.SURVEY_PRIVACYPOLICY_SWITCH_UPDATE: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					privacyPolicy: {
						...state.surveyEditModel.privacyPolicy,
						isActive: action.payload,
					},
					isPrivacyPolicyUpdated: true,
				},
			};
		}
		case ActionTypes.SURVEY_PRIVACYPOLICY_COPY_UPDATE:
		case ActionTypes.SURVEY_PRIVACYPOLICY_CLEAR_UPDATE: {
			return {
				...state,
				lastSavedSurveyEditModel: {
					...state.surveyEditModel,
					privacyPolicy: {
						...action.payload,
						isActive: true,
					},
				},
				surveyEditModel: {
					...state.surveyEditModel,
					privacyPolicy: {
						...action.payload,
						isActive: true,
					},
					isPrivacyPolicyUpdated: true,
				},
			};
		}
		case ActionTypes.SURVEY_PRIVACYPOLICY_TITLE_CHANGE: {
			const originalPrivacyPolicyTitle: ITranslationEditItem = state.lastSavedSurveyEditModel.privacyPolicy.title
				? { ...state.lastSavedSurveyEditModel.privacyPolicy.title }
				: { ...InitDefaultTranslationEditItem(state.surveyEditModel.defaultCultureCode) };
			const updatedPrivacyPolicyTitle = { ...originalPrivacyPolicyTitle, textValue: action.payload };
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					privacyPolicy: {
						...state.surveyEditModel.privacyPolicy,
						title: {
							...updatedPrivacyPolicyTitle,
							editState: TranslationHelper.getEditState(updatedPrivacyPolicyTitle, originalPrivacyPolicyTitle),
						},
					},
					isPrivacyPolicyUpdated: true,
				},
			};
		}
		case ActionTypes.SURVEY_PRIVACYPOLICY_CONTENT_CHANGE: {
			const originalPrivacyPolicyContent: ITranslationEditItem = state.lastSavedSurveyEditModel.privacyPolicy.content
				? { ...state.lastSavedSurveyEditModel.privacyPolicy.content }
				: { ...InitDefaultTranslationEditItem(state.surveyEditModel.defaultCultureCode) };
			const updatedPrivacyPolicyContent = { ...originalPrivacyPolicyContent, textValue: action.payload };
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					privacyPolicy: {
						...state.surveyEditModel.privacyPolicy,
						content: {
							...updatedPrivacyPolicyContent,
							editState: TranslationHelper.getEditState(updatedPrivacyPolicyContent, originalPrivacyPolicyContent),
						},
					},
					isPrivacyPolicyUpdated: true,
				},
			};
		}

		case ActionTypes.SURVEY_PRIVACYPOLICY_CHECKBOX_CHANGE: {
			const originalPrivacyPolicyCheckBox: ITranslationEditItem = state.lastSavedSurveyEditModel.privacyPolicy.checkboxText
				? { ...state.lastSavedSurveyEditModel.privacyPolicy.checkboxText }
				: { ...InitDefaultTranslationEditItem(state.surveyEditModel.defaultCultureCode) };
			const updatedPrivacyPolicyCheckBox = { ...originalPrivacyPolicyCheckBox, textValue: action.payload };
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					privacyPolicy: {
						...state.surveyEditModel.privacyPolicy,
						checkboxText: {
							...updatedPrivacyPolicyCheckBox,
							editState: TranslationHelper.getEditState(updatedPrivacyPolicyCheckBox, originalPrivacyPolicyCheckBox),
						},
					},
					isPrivacyPolicyUpdated: true,
				},
			};
		}

		case ActionTypes.SURVEY_PRIVACYPOLICY_ADMIN_SAVED: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					privacyPolicy: { ...action.payload },
					isPrivacyPolicyUpdated: false,
				},
			};
		}

		case ActionTypes.SURVEY_PRIVACYPOLICY_TRANSLATION_UPDATED: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					isPrivacyPolicyUpdated: true,
				},
			};
		}

		case ActionTypes.SURVEY_DEFINITIONS_SWITCH_UPDATE: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					surveyTerm: {
						...state.surveyEditModel.surveyTerm,
						isActive: action.payload.checked,
						isLoading: false,
					},
					surveyTermItems: action.payload.initialValue?.length ? action.payload.initialValue : state.surveyEditModel.surveyTermItems,
					isSurveyTermUpdated: true,
				},
			};
		}

		case ActionTypes.SURVEY_DEFINITIONS_INITIALIZE_REQUESTED: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					surveyTerm: {
						...state.surveyEditModel.surveyTerm,
						isLoading: true,
					},
				},
			};
		}

		case ActionTypes.SURVEY_DEFINITIONS_INITIALIZE_FAILED: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					surveyTerm: {
						...state.surveyEditModel.surveyTerm,
						isLoading: false,
					},
				},
			};
		}

		case ActionTypes.SURVEY_TERM_PAGE_DESCRIPTION_CHANGE: {
			const originalPageDescription: ITranslationEditItem = state.lastSavedSurveyEditModel.surveyTerm.pageDescription
				? { ...state.lastSavedSurveyEditModel.surveyTerm.pageDescription }
				: { ...InitDefaultTranslationEditItem(state.surveyEditModel.defaultCultureCode) };
			const updatedPageDescription = { ...originalPageDescription, textValue: action.payload };
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					surveyTerm: {
						...state.surveyEditModel.surveyTerm,
						pageDescription: {
							...updatedPageDescription,
							editState: TranslationHelper.getEditState(updatedPageDescription, originalPageDescription),
						},
					},
					isSurveyTermUpdated: true,
				},
			};
		}
		case ActionTypes.SURVEY_TERM_PAGE_DESCRIPTION_UPDATE:
			return {
				...state,
				lastSavedSurveyEditModel: {
					...state.lastSavedSurveyEditModel,
					surveyTerm: {
						...state.lastSavedSurveyEditModel.surveyTerm,
						pageDescription: {
							...action.payload,
							editState: EditState.None,
						},
					},
				},
				surveyEditModel: {
					...state.surveyEditModel,
					surveyTerm: {
						...state.surveyEditModel.surveyTerm,
						pageDescription: {
							...action.payload,
							editState: EditState.None,
						},
					},
					isSurveyTermUpdated: true,
				},
			};
		case ActionTypes.SURVEY_TERM_TRANSLATION_UPDATED: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					isSurveyTermUpdated: true,
				},
			};
		}
		case ActionTypes.SURVEY_TERM_ITEMS_FETCH: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					surveyTermItems: action.payload,
				},
			};
		}
		case ActionTypes.SURVEY_TERM_ADMIN_SAVED: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					surveyTerm: { ...action.payload, isLoading: false },
					isSurveyTermUpdated: false,
				},
			};
		}

		case ActionTypes.SURVEY_TERM_ADMIN_LOADING: {
			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					surveyTerm: { ...state.surveyEditModel.surveyTerm, isLoading: true },
				},
			};
		}

		case ActionTypes.SURVEY_TERM_ITEM_ADD: {
			const withAddedSurveyTermItems = [...state.surveyEditModel.surveyTermItems];
			withAddedSurveyTermItems.push({
				surveyTermItemId: action.payload.surveyTermItemId,
				sequence: action.payload.sequence,
				term: {
					textValue: '',
					cultureCode: defaultCulture.cultureCode,
					language: defaultCulture.name,
					editState: EditState.Add,
				},
				description: {
					textValue: '',
					cultureCode: defaultCulture.cultureCode,
					language: defaultCulture.name,
					editState: EditState.Add,
				},
			});

			withAddedSurveyTermItems.sort((term) => term.sequence);

			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					surveyTermItems: withAddedSurveyTermItems,
					isSurveyTermUpdated: true,
				},
			};
		}

		case ActionTypes.SURVEY_TERM_ITEM_DELETE: {
			const surveyTermItems = [...state.surveyEditModel.surveyTermItems];
			const surveyTermItemId = action.payload.surveyTermItemId;
			const sequence = action.payload.sequence;
			const removePosition = surveyTermItems.findIndex((term) => term.surveyTermItemId === surveyTermItemId && term.sequence === sequence);

			surveyTermItems.splice(removePosition, 1, action.payload);

			return {
				...state,
				surveyEditModel: {
					...state.surveyEditModel,
					surveyTermItems,
					isSurveyTermUpdated: true,
				},
			};
		}

		case ActionTypes.SURVEY_TERM_ITEM_CHANGE: {
			const defaultCultureCode = action.payload.defaultCultureCode;
			const editFieldType = action.payload.surveyTermItemEditFieldType;
			const surveyTermItemId = action.payload.surveyTermItemId;
			const sequence = action.payload.sequence;
			const surveyTermItems = [...state.surveyEditModel.surveyTermItems];
			const termToUpdate = surveyTermItems.find((def) => def.surveyTermItemId === surveyTermItemId && def.sequence === sequence);
			const originalTerm = state.lastSavedSurveyEditModel.surveyTermItems?.find(
				(def) => def.surveyTermItemId === surveyTermItemId && def.sequence === sequence,
			);

			// the term/description can be null, and this will broke the call to TranslationHelper.getEditState(...)
			const refinedOriginalTerm = originalTerm
				? {
						...originalTerm,
						term: originalTerm?.term
							? originalTerm.term
							: {
									textValue: '',
									editState: EditState.Add,
									language: defaultCultureCode,
									cultureCode: defaultCultureCode,
							  },
						description: originalTerm?.description
							? originalTerm.description
							: {
									textValue: '',
									editState: EditState.Add,
									language: defaultCultureCode,
									cultureCode: defaultCultureCode,
							  },
				  }
				: undefined;

			const updatePosition = surveyTermItems.findIndex((def) => def.surveyTermItemId === surveyTermItemId && def.sequence === sequence);

			if (termToUpdate) {
				if (editFieldType === SurveyTermItemEditFieldType.SURVEY_DEFINITION_TERM) {
					const newTranslation = {
						...termToUpdate.term,
						textValue: action.payload.surveyTermItemEditValue,
						cultureCode: defaultCultureCode,
						language: termToUpdate.term?.language ?? defaultCultureCode,
						editState: termToUpdate.term?.editState ?? EditState.Add,
					};

					const termWithNewTranslation = {
						...termToUpdate,
						term: {
							...newTranslation,
							editState: refinedOriginalTerm ? TranslationHelper.getEditState(newTranslation, refinedOriginalTerm.term) : EditState.Add,
						},
					};

					surveyTermItems.splice(updatePosition, 1, termWithNewTranslation);

					return {
						...state,
						surveyEditModel: {
							...state.surveyEditModel,
							surveyTermItems,
							isSurveyTermUpdated: true,
						},
					};
				} else if (editFieldType === SurveyTermItemEditFieldType.SURVEY_DEFINITION_DESCRIPTION) {
					const newTranslation = {
						...termToUpdate.description,
						textValue: action.payload.surveyTermItemEditValue,
						cultureCode: defaultCultureCode,
						language: termToUpdate.term?.language ?? defaultCultureCode,
						editState: termToUpdate.term?.editState ?? EditState.Add,
					};

					const descriptionWithNewTranslation = {
						...termToUpdate,
						description: {
							...newTranslation,
							editState: refinedOriginalTerm
								? TranslationHelper.getEditState(newTranslation, refinedOriginalTerm.description)
								: EditState.Add,
						},
					};

					surveyTermItems.splice(updatePosition, 1, descriptionWithNewTranslation);

					return {
						...state,
						surveyEditModel: {
							...state.surveyEditModel,
							surveyTermItems,
							isSurveyTermUpdated: true,
						},
					};
				}
			} else {
				return state;
			}
		}
	}
	return state;
};

export const updateQuestionDimensionNames = (question: SurveyQuestionEditModel, dimensionName: string) => {
	if (!question.dimensionNames) {
		question.dimensionNames = [dimensionName];
	} else if (question.dimensionNames.indexOf(dimensionName) < 0) {
		question.dimensionNames.push(dimensionName);
	}
	return question;
};

export const selectSurveyQuestion = createSelector(
	[(state: RootState) => state.survey.surveyEditModel.questions, (_state, surveyQuestionId?: number) => surveyQuestionId],
	(surveyQuestions, surveyQuestionId): ISurveyQuestionEditModel | undefined => {
		if (!surveyQuestionId) {
			return undefined;
		}

		return surveyQuestions.find((q) => q.surveyQuestionId === surveyQuestionId);
	},
);
